import { createSlice } from '@reduxjs/toolkit';
import { RequestStatus } from '../../shared/enums/request-status';
import {
  getVerifyEmailRequest,
  emailVerifierUploadedFilesListRequest,
  downloadEmailVerifiedFileRequest,
  uploadedFileCsvRequest,
  getVerifiedEmailFileDetailsByIdRequest,
} from './extra-action';
import { GetUploadedFilePayload } from './types';

interface RequestState {
  status: RequestStatus;
  message: string;
  error: any;
}

interface State {
  // ** Request States
  getVerifyEmailRequest: RequestState;
  emailVerifierUploadedFilesListRequest: RequestState;
  uploadedFileCsvRequest: RequestState;
  downloadEmailVerifiedFileRequest: RequestState;
  getVerifiedEmailFileDetailsByIdRequest: RequestState;

  // ** Data States
  emailVerifierUploadedFilesList: {
    emailVerifications: any;
    total: number;
  };
  isVerifiedEmailData: string[];
  setSearchPaginationState: GetUploadedFilePayload;
}

const initialState: State = {
  // ** Request Initial States
  getVerifyEmailRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  emailVerifierUploadedFilesListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  uploadedFileCsvRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  downloadEmailVerifiedFileRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getVerifiedEmailFileDetailsByIdRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },

  // ** Data Initial States
  emailVerifierUploadedFilesList: {
    emailVerifications: [],
    total: 0,
  },
  isVerifiedEmailData: null,
  setSearchPaginationState: null,
};

const emailVerifierSlice = createSlice({
  name: 'emailVerifier',
  initialState,
  reducers: {
    resetVerifyEmailResuest: (state) => {
      state.getVerifyEmailRequest = initialState.getVerifyEmailRequest;
    },
    resetUploadedFileRequest: (state) => {
      state.emailVerifierUploadedFilesListRequest =
        initialState.emailVerifierUploadedFilesListRequest;
    },
    resetUploadedFileCsvRequest: (state) => {
      state.uploadedFileCsvRequest = initialState.uploadedFileCsvRequest;
    },
    setIsVerifiedEmailData: (state, action) => {
      state.isVerifiedEmailData = action.payload;
    },
    resetDownloadEmailVerifiedFileRequest: (state) => {
      state.downloadEmailVerifiedFileRequest =
        initialState.downloadEmailVerifiedFileRequest;
    },
    resetPagination: (state) => {
      state.setSearchPaginationState = initialState.setSearchPaginationState;
    },
    searchPaginationState: (state, action) => {
      state.setSearchPaginationState = action.payload;
    },
  },

  extraReducers: (builder) => {
    // Email Verify
    builder.addCase(getVerifyEmailRequest.pending, (state) => {
      state.getVerifyEmailRequest.status = RequestStatus.Pending;
      state.getVerifyEmailRequest.error = null;
    });

    builder.addCase(getVerifyEmailRequest.fulfilled, (state, action) => {
      state.getVerifyEmailRequest.status = RequestStatus.Succeeded;
      state.getVerifyEmailRequest.message = action.payload.message;
    });

    builder.addCase(getVerifyEmailRequest.rejected, (state, action) => {
      state.getVerifyEmailRequest.status = RequestStatus.Failed;
      state.getVerifyEmailRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Uploaded File
    builder.addCase(emailVerifierUploadedFilesListRequest.pending, (state) => {
      state.emailVerifierUploadedFilesListRequest.status =
        RequestStatus.Pending;
      state.emailVerifierUploadedFilesListRequest.error = null;
    });
    builder.addCase(
      emailVerifierUploadedFilesListRequest.fulfilled,
      (state, action) => {
        state.emailVerifierUploadedFilesListRequest.status =
          RequestStatus.Succeeded;
        state.emailVerifierUploadedFilesListRequest.message =
          action.payload.message;
        state.emailVerifierUploadedFilesList.emailVerifications =
          action.payload?.payload?.emailVerifications || [];
        state.emailVerifierUploadedFilesList.total =
          action.payload?.payload?.total || 0;
      },
    );

    builder.addCase(
      emailVerifierUploadedFilesListRequest.rejected,
      (state, action) => {
        state.emailVerifierUploadedFilesListRequest.status =
          RequestStatus.Failed;
        state.emailVerifierUploadedFilesListRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Upload Csv File
    builder.addCase(uploadedFileCsvRequest.pending, (state) => {
      state.uploadedFileCsvRequest.status = RequestStatus.Pending;
      state.uploadedFileCsvRequest.error = null;
    });

    builder.addCase(uploadedFileCsvRequest.fulfilled, (state, action) => {
      state.uploadedFileCsvRequest.status = RequestStatus.Succeeded;
      state.uploadedFileCsvRequest.message = action.payload.message;
    });

    builder.addCase(uploadedFileCsvRequest.rejected, (state, action) => {
      state.uploadedFileCsvRequest.status = RequestStatus.Failed;
      state.uploadedFileCsvRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Download Email Verified File
    builder.addCase(downloadEmailVerifiedFileRequest.pending, (state) => {
      state.downloadEmailVerifiedFileRequest.status = RequestStatus.Pending;
      state.downloadEmailVerifiedFileRequest.error = null;
    });
    builder.addCase(
      downloadEmailVerifiedFileRequest.fulfilled,
      (state, action) => {
        state.downloadEmailVerifiedFileRequest.status = RequestStatus.Succeeded;
        state.downloadEmailVerifiedFileRequest.message = action.payload.message;
      },
    );
    builder.addCase(
      downloadEmailVerifiedFileRequest.rejected,
      (state, action) => {
        state.downloadEmailVerifiedFileRequest.status = RequestStatus.Failed;
        state.downloadEmailVerifiedFileRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get Verified Email File Details By Id
    builder.addCase(getVerifiedEmailFileDetailsByIdRequest.pending, (state) => {
      state.getVerifiedEmailFileDetailsByIdRequest.status =
        RequestStatus.Pending;
      state.getVerifiedEmailFileDetailsByIdRequest.error = null;
    });
    builder.addCase(
      getVerifiedEmailFileDetailsByIdRequest.fulfilled,
      (state, action) => {
        state.getVerifiedEmailFileDetailsByIdRequest.status =
          RequestStatus.Succeeded;
        state.getVerifiedEmailFileDetailsByIdRequest.message =
          action.payload.message;
        const updatedFileDetails =
          action.payload?.payload?.emailVerification?.[0] || null;
        if (updatedFileDetails) {
          state.emailVerifierUploadedFilesList.emailVerifications = state.emailVerifierUploadedFilesList.emailVerifications.map(
            (item) => {
              if (item.id === updatedFileDetails.id) {
                return updatedFileDetails;
              }
              return item;
            },
          );
        }
      },
    );
    builder.addCase(
      getVerifiedEmailFileDetailsByIdRequest.rejected,
      (state, action) => {
        state.getVerifiedEmailFileDetailsByIdRequest.status =
          RequestStatus.Failed;
        state.getVerifiedEmailFileDetailsByIdRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );
  },
});
export const {
  resetVerifyEmailResuest,
  resetUploadedFileRequest,
  resetUploadedFileCsvRequest,
  setIsVerifiedEmailData,
  resetDownloadEmailVerifiedFileRequest,
  resetPagination,
  searchPaginationState,
} = emailVerifierSlice.actions;

export default emailVerifierSlice.reducer;
